import { LANDING_VARIANT } from "@/content/marketing/landingVariant";

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

/** GTM container id format. Single source of truth for the validation. */
export const GTM_ID_PATTERN = /^GTM-[A-Z0-9]+$/;

/**
 * Validated GTM container id, or `undefined` if unset/malformed. Shared by the
 * SSR bootstrap-snippet gate in `__root.tsx` and the `dataLayer` push gate
 * below, so both agree on what counts as a valid container id — a malformed
 * `VITE_GTM_ID` is rejected everywhere rather than skipping the snippet but
 * still pushing to `dataLayer`. Returns `undefined` instead of throwing because
 * the bootstrap caller runs inside a React component on every SSR request (not
 * once at build time), where a throw would 500 every request.
 */
export function getGtmContainerId(): string | undefined {
  const id = import.meta.env.VITE_GTM_ID as string | undefined;
  if (!id || !GTM_ID_PATTERN.test(id)) return undefined;
  return id;
}

/**
 * The inline GTM bootstrap `__root.tsx` renders into `<head>`. It seeds
 * `landing_variant` on the dataLayer BEFORE `gtm.start`, so the GA4 tag's
 * initial `page_view` can read it as a dataLayer variable (#1475).
 */
export function gtmBootstrapScript(gtmId: string): string {
  return `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({landing_variant:${JSON.stringify(LANDING_VARIANT)}});w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${gtmId}');`;
}

function dataLayer(): Record<string, unknown>[] | null {
  if (!getGtmContainerId()) return null;
  if (typeof window === "undefined") return null;
  window.dataLayer = window.dataLayer ?? [];
  return window.dataLayer;
}

export function pushGtm(payload: Record<string, unknown>): void {
  const dl = dataLayer();
  if (!dl) return;
  dl.push(payload);
}

/** Only known route shapes reach analytics; unknown paths fail closed. */
function analyticsPath(rawPath: string): string {
  const path = rawPath.split(/[?#]/, 1)[0].replace(/\/$/, "") || "/";
  const routes: [RegExp, string][] = [
    [/^\/onboarding\/[^/]+\/(name|purpose|design|domain|updates|first-post)$/, "/onboarding/:workspaceId/$1"],
    [/^\/dashboard\/w\/[^/]+\/posts\/new$/, "/dashboard/w/:workspaceId/posts/new"],
    [/^\/dashboard\/w\/[^/]+\/posts\/[^/]+$/, "/dashboard/w/:workspaceId/posts/:postId"],
    [/^\/dashboard\/w\/[^/]+\/subscribers\/[^/]+\/[^/]+$/, "/dashboard/w/:workspaceId/subscribers/:domain/:email"],
    [/^\/dashboard\/w\/[^/]+(\/(?:analytics|api-keys|bounces|configuration|design|developer|domains|emails|events|integrations|newsletter|posts|publish|sending|settings|subscribers|templates))?(\/(?:access|email|mcp|webhook|newsletter|api-keys|bounces|domains|events|templates))?$/, "/dashboard/w/:workspaceId$1$2"],
    [/^\/preview\/[^/]+\/[^/]+$/, "/preview/:workspaceId/:postId"],
    [/^\/preview\/[^/]+$/, "/preview/:postId"],
    [/^\/(approve|approve-link)\/[^/]+$/, "/$1/:hash"],
    [/^\/blog\/(categories|tags)\/[^/]+$/, "/blog/$1/:term"],
    [/^\/blog\/(?!categories$|tags$|rss\.xml$|sitemap\.xml$)[^/]+$/, "/blog/:slug"],
  ];
  for (const [pattern, replacement] of routes) {
    if (pattern.test(path)) return path.replace(pattern, replacement);
  }
  if (/^\/(?:about|agents|ai-blogging-platform|faq|platform|pricing|privacy|terms|login|team-login|linkedin-callback|account-deleted|waitlist\/joined|404|500|blog(?:\/(?:categories|tags|rss\.xml|sitemap\.xml))?|dashboard(?:\/(?:domains\/cloudflare-callback|integrations\/(?:linkedin-callback|x-callback)))?)?$/.test(path)) {
    return path;
  }
  return "/redacted";
}

function analyticsLocation(rawLocation: string): string {
  try {
    const url = new URL(rawLocation);
    if (url.protocol !== "https:" && url.protocol !== "http:") return "";
    return `${url.origin}${analyticsPath(url.pathname)}`;
  } catch {
    return "";
  }
}

/** SPA client-side navigations (initial load is covered by the GTM/GA4 page_view tag). */
export function pushVirtualPageView(args: {
  pagePath: string;
  pageTitle?: string;
  pageLocation?: string;
}): void {
  pushGtm({
    event: "virtual_page_view",
    page_path: analyticsPath(args.pagePath),
    // Titles can contain post/workspace names, or still belong to the prior route.
    page_title: "",
    page_location: analyticsLocation(
      args.pageLocation ??
      (typeof window !== "undefined" ? window.location.href : ""),
    ),
    landing_variant: LANDING_VARIANT,
  });
}

export type AuthMethod = "google" | "linkedin" | "password";

/** GA4 recommended `sign_up`: `createUser` created the account just now. */
export function pushSignUpEvent(args: { method: AuthMethod }): void {
  pushGtm({
    event: "sign_up",
    method: args.method,
    landing_variant: LANDING_VARIANT,
  });
}

/** GA4 recommended `login`: an existing account signed in again. */
export function pushLoginEvent(args: { method: AuthMethod }): void {
  pushGtm({
    event: "login",
    method: args.method,
  });
}

/**
 * Call after createUser succeeds (full GoodSender login). Its `isNewUser`
 * picks the event, so one sign-in is counted as a sign-up or a login, never
 * both.
 */
export function pushAuthSuccessEvent(args: {
  method: AuthMethod;
  isNewUser: boolean;
}): void {
  if (args.isNewUser) pushSignUpEvent({ method: args.method });
  else pushLoginEvent({ method: args.method });
}

/** A sign-in surface opened: the dialog, or the /login and /team-login pages. */
export function pushSignupStart(): void {
  pushGtm({ event: "signup_start" });
}

/** Live onboarding screens and the legacy domain redirect on actual navigation. */
export type OnboardingStep =
  | "name"
  | "purpose"
  | "design"
  | "domain"
  | "updates"
  | "first-post";

export function pushOnboardingStep(args: { step: OnboardingStep }): void {
  pushGtm({ event: "onboarding_step", step: args.step });
}

/** The first-post step handed off — onboarding is behind the user. */
export function pushOnboardingComplete(): void {
  pushGtm({ event: "onboarding_complete" });
}

/** The workspace's first post went live. */
export function pushFirstPostPublished(): void {
  pushGtm({ event: "first_post_published" });
}

export function pushCtaClick(args: {
  ctaLocation: string;
  ctaLabel: string;
}): void {
  pushGtm({
    event: "cta_click",
    cta_location: args.ctaLocation,
    cta_label: args.ctaLabel,
  });
}
